import {
  type ADFDocumentContentNode,
  type ADFDocument,
  type ADFTextNode,
} from '@canvas/adf-types';

import { adfFromString } from './from-string.ts';

/**
 * Converts an ADF string to markdown.
 *
 * @description Converts to JSON and delgates to {@link toMarkdown}
 *
 * @param doc
 * @returns markdown string
 */
export const toMarkdownFromString = (doc: string) => {
  const adf = adfFromString(doc);
  return toMarkdown(adf);
};

/**
 * Converts an ADF document to markdown.
 *
 * @description NOTE: Only supports text nodes as leaf nodes.
 *
 * @param doc
 * @returns markdown string
 */
export const toMarkdown = (doc: ADFDocument) => {
  let markdown = '';

  for (const node of doc.content) {
    markdown += printNode(node);
  }

  return markdown;
};

const printNode = (node: ADFDocumentContentNode) => {
  let markdown = '';

  // -> text + '\n
  if (node.type === 'paragraph') {
    for (const n of node.content ?? []) {
      if (n.type !== 'text') {
        continue;
      }

      markdown += getText(n);
    }

    markdown += '\n';
  }

  // -> '#{n}' + text
  else if (node.type === 'heading') {
    const level = node.attrs.level;
    markdown += `${new Array(level)
      .fill(0)
      .map(() => '#')
      .join('')} `;

    for (const n of node.content ?? []) {
      if (n.type !== 'text') {
        continue;
      }

      markdown += getText(n);
    }

    markdown += '\n';
  }

  // -> '<n>. ' + text
  else if (node.type === 'orderedList') {
    const items = node.content ?? [];
    let order = node.attrs?.order ?? 1;

    for (const item of items) {
      markdown += `${order}. `;
      markdown += printNode(item);
      order++;
    }
  }

  // -> '-' + text
  else if (node.type === 'bulletList') {
    const items = node.content ?? [];
    for (const item of items) {
      markdown += `- `;
      markdown += printNode(item);
    }
  }

  // -> text
  else if (node.type === 'listItem') {
    const items = node.content ?? [];
    for (const item of items) {
      markdown += printNode(item);
    }
  }

  return markdown;
};

const getText = (node: ADFTextNode) => {
  let text = node.text;

  for (const mark of node.marks ?? []) {
    if (mark.type === 'strong') {
      text = `**${text}**`;
    }
    if (mark.type === 'em') {
      text = `_${text}_`;
    }
    if (mark.type === 'strike') {
      text = `~${text}~`;
    }
  }

  return text;
};
